import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, PlugZap, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError } from "@hostdock/sdk";
import type { ConnectionTestResult } from "@hostdock/types";
import { Button, Checkbox, Field, FormError, Input, Notice, PageHeader, Panel, Select, Spinner, Textarea } from "@hostdock/ui";
import { api } from "../../api";
import { RequirePermission } from "../../lib/guard";
import { isTerminal, useLiveJob } from "../../lib/use-job";

export function AddServerPage() {
  return (
    <RequirePermission anyOf={["SERVER_ADD"]}>
      <AddServer />
    </RequirePermission>
  );
}

function parseLabels(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split(/[\n,]/)) {
    const [k, ...rest] = line.split("=");
    if (k?.trim()) out[k.trim()] = rest.join("=").trim();
  }
  return out;
}

function AddServer() {
  const navigate = useNavigate();
  const regions = useQuery({ queryKey: ["regions"], queryFn: api.regions.list });
  const [f, setF] = useState({
    name: "", hostname: "", regionId: "", publicIp: "", privateIp: "", sshPort: "22", sshUser: "root",
    authType: "KEY" as "KEY" | "PASSWORD", privateKey: "", passphrase: "", password: "", labels: "", mongodbEnabled: false, autoActivate: true, agentConnectivity: "AUTO" as "AUTO" | "DIRECT" | "TUNNEL",
  });
  const [testJobId, setTestJobId] = useState<string | null>(null);
  const [testedFor, setTestedFor] = useState<string>("");

  const target = useMemo(
    () => ({
      publicIp: f.publicIp.trim(),
      sshPort: Number(f.sshPort) || 22,
      sshUser: f.sshUser.trim(),
      auth: f.authType === "KEY"
        ? { type: "KEY" as const, privateKey: f.privateKey, ...(f.passphrase ? { passphrase: f.passphrase } : {}) }
        : { type: "PASSWORD" as const, password: f.password },
    }),
    [f.publicIp, f.sshPort, f.sshUser, f.authType, f.privateKey, f.passphrase, f.password],
  );
  // A test result is only valid for exactly the target and credentials it was run with.
  const targetKey = JSON.stringify(target);
  const liveJob = useLiveJob(testJobId);
  const test = useMutation({
    mutationFn: () => api.servers.testConnection(target),
    onSuccess: (job) => {
      setTestJobId(job.id);
      setTestedFor(targetKey);
    },
  });
  const testResult = liveJob?.status === "SUCCESS" && testedFor === targetKey ? (liveJob.result as unknown as ConnectionTestResult) : null;
  const testFailed = liveJob?.status === "FAILED" && testedFor === targetKey ? liveJob.error : null;
  const testing = test.isPending || (testJobId !== null && testedFor === targetKey && !isTerminal(liveJob?.status));

  const create = useMutation({ meta: { inlineError: true },
    mutationFn: () =>
      api.servers.create({
        ...target,
        name: f.name,
        hostname: f.hostname,
        regionId: f.regionId,
        privateIp: f.privateIp.trim() || null,
        labels: parseLabels(f.labels),
        hostKeyFingerprint: testResult!.hostKeyFingerprint!,
        mongodbEnabled: f.mongodbEnabled,
        agentConnectivity: f.agentConnectivity,
        autoActivate: f.autoActivate,
      }),
    onSuccess: (res) => navigate(`/infrastructure/servers/${res.server.id}?tab=provisioning`, { replace: true }),
  });
  const fe = { ...(test.error instanceof ApiError ? test.error.fieldErrors : {}), ...(create.error instanceof ApiError ? create.error.fieldErrors : {}) };
  const upd = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }));
  const activeRegions = regions.data?.items.filter((r) => r.status === "ACTIVE") ?? [];

  return (
    <>
      <PageHeader title="Add server" description="HostDock connects over SSH, installs its own key, then provisions the node automatically." />
      <div className="grid max-w-5xl gap-5 lg:grid-cols-[1fr_320px]">
        <div className="flex flex-col gap-5">
          <Panel title="Server">
            {regions.data && activeRegions.length === 0 && (
              <div className="mb-4">
                <Notice tone="warning" title="No active regions" action={<Link to="/infrastructure/regions" className="text-[13px] text-accent hover:underline">Create one</Link>}>
                  Every server belongs to a region.
                </Notice>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Server name" hint="e.g. BD-NODE-07" error={fe.name}>{(p) => <Input {...p} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value.toUpperCase() })} className="font-mono" autoFocus />}</Field>
              <Field label="Region" error={fe.regionId}>
                {(p) => (
                  <Select {...p} value={f.regionId} onChange={upd("regionId")}>
                    <option value="">Select a region…</option>
                    {activeRegions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </Select>
                )}
              </Field>
              <Field label="Hostname" hint="Fully qualified name, e.g. bd-node-07.example.net" error={fe.hostname}>{(p) => <Input {...p} value={f.hostname} onChange={upd("hostname")} />}</Field>
              <Field label="Public IP" error={fe.publicIp}>{(p) => <Input {...p} value={f.publicIp} onChange={upd("publicIp")} className="font-mono" inputMode="decimal" />}</Field>
              <Field label="Private IP (optional)" error={fe.privateIp}>{(p) => <Input {...p} value={f.privateIp} onChange={upd("privateIp")} className="font-mono" />}</Field>
              <Field label="Labels (optional)" hint="key=value, one per line" error={fe.labels}>{(p) => <Textarea {...p} value={f.labels} onChange={upd("labels")} rows={2} className="font-mono text-[12.5px]" placeholder="tier=standard" />}</Field>
            </div>
          </Panel>
          <Panel title="SSH connection" description="Root or a user with passwordless sudo. Credentials are encrypted and used only for the initial bootstrap.">
            <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
              <Field label="SSH user" error={fe.sshUser}>{(p) => <Input {...p} value={f.sshUser} onChange={upd("sshUser")} className="font-mono" autoComplete="off" />}</Field>
              <Field label="SSH port" error={fe.sshPort}>{(p) => <Input {...p} value={f.sshPort} onChange={upd("sshPort")} inputMode="numeric" />}</Field>
            </div>
            <div className="mt-4 flex flex-col gap-4">
              <Field label="Authentication">
                {(p) => (
                  <Select {...p} value={f.authType} onChange={(e) => setF({ ...f, authType: e.target.value as "KEY" | "PASSWORD" })} className="w-52">
                    <option value="KEY">Private key</option>
                    <option value="PASSWORD">Password</option>
                  </Select>
                )}
              </Field>
              {f.authType === "KEY" ? (
                <>
                  <Field label="Private key" hint="OpenSSH or PEM format. HostDock installs its own per-server key and stops using this one after bootstrap." error={fe["auth.privateKey"]}>
                    {(p) => <Textarea {...p} value={f.privateKey} onChange={upd("privateKey")} rows={5} className="font-mono text-[11.5px]" spellCheck={false} autoComplete="off" placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" />}
                  </Field>
                  <Field label="Key passphrase (optional)">{(p) => <Input {...p} type="password" value={f.passphrase} onChange={upd("passphrase")} autoComplete="off" />}</Field>
                </>
              ) : (
                <Field label="Password" hint="Used once to install the HostDock key; password login is then disabled on the server." error={fe["auth.password"]}>
                  {(p) => <Input {...p} type="password" value={f.password} onChange={upd("password")} autoComplete="off" />}
                </Field>
              )}
            </div>
          </Panel>
          <Panel title="Options">
            <div className="flex flex-col gap-3">
              <Checkbox checked={f.autoActivate} onChange={(v) => setF({ ...f, autoActivate: v })} label="Activate automatically when production ready" description="Otherwise the server waits in READY until you activate it." />
              <Checkbox checked={f.mongodbEnabled} onChange={(v) => setF({ ...f, mongodbEnabled: v })} label="Enable MongoDB on this server" />
              <Field label="Agent connection" hint="Automatic tests from the server whether it can reach the control plane directly, and otherwise carries the agent's connection over SSH.">
                {(p) => (
                  <Select {...p} value={f.agentConnectivity} onChange={(e) => setF({ ...f, agentConnectivity: e.target.value as "AUTO" })}>
                    <option value="AUTO">Automatic (recommended)</option>
                    <option value="DIRECT">Direct only</option>
                    <option value="TUNNEL">Always use SSH tunnel</option>
                  </Select>
                )}
              </Field>
            </div>
          </Panel>
        </div>

        <div className="flex flex-col gap-4 lg:sticky lg:top-4 lg:self-start">
          <Panel title="Connection check">
            <div className="flex flex-col gap-3">
              <Button icon={<PlugZap size={15} />} loading={testing} disabled={!target.publicIp || !target.sshUser} onClick={() => test.mutate()}>
                Test connection
              </Button>
              {testing && <Spinner label={liveJob?.progressMessage ?? "Queued"} />}
              {testFailed && (
                <div className="flex items-start gap-2 text-[13px] text-danger">
                  <XCircle size={16} className="mt-0.5 shrink-0" /> {testFailed}
                </div>
              )}
              {testResult && (
                <div className="flex flex-col gap-2 text-[13px]">
                  <div className={`flex items-start gap-2 ${testResult.ok ? "text-success" : "text-warning"}`}>
                    {testResult.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
                    <span>{testResult.message}</span>
                  </div>
                  <div className="rounded-md border border-line bg-surface-2 p-2.5">
                    <p className="text-[12px] text-muted">Host key fingerprint (will be pinned)</p>
                    <p className="font-mono text-[11.5px] break-all">{testResult.hostKeyFingerprint}</p>
                    <p className="mt-1 text-[11.5px] text-subtle">Compare with <code>ssh-keygen -lf /etc/ssh/ssh_host_ed25519_key.pub</code> on the server.</p>
                  </div>
                  <p className="text-[12px] text-muted">{testResult.os} · kernel {testResult.kernel} · {testResult.arch}</p>
                </div>
              )}
              <FormError error={test.error && !Object.keys(fe).length ? test.error : null} />
            </div>
          </Panel>
          <Button
            variant="primary"
            loading={create.isPending}
            disabled={!testResult?.ok || !f.name || !f.hostname || !f.regionId}
            onClick={() => create.mutate()}
          >
            Add &amp; provision
          </Button>
          {!testResult?.ok && <p className="text-[12px] text-subtle">Run a successful connection test first.</p>}
          {create.error && !Object.keys(fe).length ? <FormError error={create.error} /> : null}
        </div>
      </div>
    </>
  );
}
